import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from '../lib/api';
import type { Comment, EventPayloadMap, EventType, PopulatedComment } from '../types';

/**
 * Custom hook to fetch all comments for a project and provide helper functions
 * to access them by checklist item number
 */
export function useValidationComments(projectId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['project-comments', projectId];

  // Fetch all comments for the project in a single request
  const {
    data: allComments,
    error,
    isLoading,
  } = useQuery<Comment[]>({
    queryKey,
    queryFn: () => api.getCommentsByProject(projectId).then((res) => res.items),
    refetchOnWindowFocus: true,
  });

  // Organize comments by checklist item number for efficient lookup
  const commentsByItem = useMemo(() => {
    if (!allComments) return {};

    const grouped: Record<string, Comment[]> = {};

    allComments.forEach((comment: Comment) => {
      if (!grouped[comment.checklistItemNumber]) {
        grouped[comment.checklistItemNumber] = [];
      }
      grouped[comment.checklistItemNumber].push(comment);
    });

    // Sort comments by date (oldest first) within each checklist item
    Object.keys(grouped).forEach((itemNumber) => {
      grouped[itemNumber].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    });

    return grouped;
  }, [allComments]);

  // Add a comment
  const addCommentMutation = useMutation({
    mutationFn: ({ checklistItemNumber, content }: { checklistItemNumber: string; content: string }) =>
      api.createComment(projectId, checklistItemNumber, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update a comment
  const editCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      api.updateComment(projectId, commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      console.error('Error editing comment:', error);
    },
  });

  // Delete a comment
  const removeCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(projectId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      console.error('Error deleting comment:', error);
    },
  });

  const addComment = useCallback(
    async (checklistItemNumber: string, content: string) => {
      await addCommentMutation.mutateAsync({ checklistItemNumber, content });
    },
    [addCommentMutation]
  );

  const editComment = useCallback(
    async (commentId: string, content: string) => {
      await editCommentMutation.mutateAsync({ commentId, content });
    },
    [editCommentMutation]
  );

  const removeComment = useCallback(
    async (commentId: string) => {
      await removeCommentMutation.mutateAsync(commentId);
    },
    [removeCommentMutation]
  );

  return {
    allComments,
    commentsByItem,
    error,
    isLoading,
    addComment,
    editComment,
    removeComment,
    isAddingComment: addCommentMutation.status === 'pending',
    isEditingComment: editCommentMutation.status === 'pending',
    isDeletingComment: removeCommentMutation.status === 'pending',
  };
}

export function handleCommentCreated(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  payload: PopulatedComment
) {
  queryClient.setQueryData<Comment[]>(['project-comments', projectId], (old = []) => {
    if (old.some((c) => c.id === payload.id)) {
      return old;
    }
    return [payload, ...old];
  });
}

export function handleCommentUpdated(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  payload: PopulatedComment
) {
  queryClient.setQueryData<Comment[]>(['project-comments', projectId], (old = []) =>
    old.map((c) => (c.id === payload.id ? payload : c))
  );
}

export function handleCommentDeleted(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  payload: EventPayloadMap[EventType.COMMENT_DELETED]
) {
  queryClient.setQueryData<Comment[]>(['project-comments', projectId], (old = []) =>
    old.filter((c) => c.id !== payload.commentId)
  );
}
